/**
 * Error Boundary for QuestionField component
 */

import { css } from 'aphrodite';
import * as React from 'react';
import { styleSheet } from '../../styles';

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
	prevResetKeys?: ReadonlyArray<unknown>;
}

interface QuestionFieldErrorBoundaryProps {
	children: React.ReactNode;
	questionIndex: number;
	/** When any value here changes, a tripped boundary resets and re-renders its children. */
	resetKeys?: ReadonlyArray<unknown>;
	onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/** True when the two resetKeys arrays differ in length or any element (by identity). */
const resetKeysChanged = (a?: ReadonlyArray<unknown>, b?: ReadonlyArray<unknown>): boolean => {
	if (a === b) return false;
	if (!a || !b || a.length !== b.length) return true;
	return a.some((v, i) => !Object.is(v, b[i]));
};

export class QuestionFieldErrorBoundary extends React.Component<QuestionFieldErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: QuestionFieldErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, prevResetKeys: props.resetKeys };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	// Reset a tripped boundary when resetKeys change so a transient render error (e.g. a brief
	// data inconsistency during a reorder) recovers automatically instead of sticking until refresh.
	static getDerivedStateFromProps(
		props: QuestionFieldErrorBoundaryProps,
		state: ErrorBoundaryState
	): Partial<ErrorBoundaryState> | null {
		if (!resetKeysChanged(props.resetKeys, state.prevResetKeys)) return null;
		return state.hasError
			? { hasError: false, error: undefined, prevResetKeys: props.resetKeys }
			: { prevResetKeys: props.resetKeys };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error(`QuestionField Error (Question ${this.props.questionIndex + 1}):`, error, errorInfo);
		this.props.onError?.(error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			return (
				<section className={css(styleSheet.bottomSection, styleSheet.bottomSectionNoValue)}>
					<div className={css(styleSheet.section, styleSheet.sectionFullWidth)}>
						<div style={{ padding: '16px', textAlign: 'center' }}>
							<h3>Error loading question {this.props.questionIndex + 1}</h3>
							<p>There was an error loading this question. Please try refreshing the page.</p>
							{process.env.NODE_ENV === 'development' && this.state.error && (
								<details style={{ marginTop: '16px', textAlign: 'left' }}>
									<summary>Error Details</summary>
									<pre style={{ fontSize: '12px', color: '#666' }}>{this.state.error.stack}</pre>
								</details>
							)}
						</div>
					</div>
				</section>
			);
		}

		return this.props.children;
	}
}
